import {PropertiesValuesForm} from '@github-ui/custom-properties-core/components/PropertiesValuesForm'
import {PropertiesValuesTable} from '@github-ui/custom-properties-core/components/PropertiesValuesTable'
import {ServerErrorFormBanner} from '@github-ui/custom-properties-core/components/ServerErrorFormBanner'
import {
  repoCustomPropertyValueValidator,
  validateRepoPropertyFormValues,
} from '@github-ui/custom-properties-core/helpers/validate'
import {useDiscardConfirmation} from '@github-ui/custom-properties-core/hooks/use-discard-confirmation'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-core/hooks/use-edit-custom-properties'
import {useSetPropertiesValuesMutation} from '@github-ui/custom-properties-core/hooks/use-set-properties-values'
import type {
  PropertiesLimits,
  PropertyDefinition,
  PropertySourceDetails,
  PropertyTarget,
  PropertyValue,
} from '@github-ui/custom-properties-types'
import {Button} from '@primer/react'
import {useMemo, useState} from 'react'

const DEFAULT_ACTION_LABELS = {save: 'Save', cancel: 'Cancel'}

export function PropertiesEditingPage({
  editingTargets,
  onClose,
  editableProperties,
  definitions,
  onSuccess,
  renderPageHeader,
  actionLabels = DEFAULT_ACTION_LABELS,
  setValuesPath,
  propertiesLimits,
  // The source level where said property can be managed, i.e. have values set
  managedBySource,
}: {
  editableProperties: string[]
  editingTargets: PropertyTarget[]
  onClose?: () => void
  definitions: PropertyDefinition[]
  onSuccess: () => void
  renderPageHeader: ({canClose}: {canClose: () => Promise<boolean>}) => React.ReactElement
  actionLabels?: {save?: string; cancel?: string}
  setValuesPath: string
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
  propertiesLimits: PropertiesLimits
}) {
  const propertyValueValidator = useMemo(
    () => repoCustomPropertyValueValidator(definitions, propertiesLimits),
    [definitions, propertiesLimits],
  )

  const formValidator = useMemo(
    () => (propertyValuesMap: Record<string, {value?: PropertyValue}>) =>
      validateRepoPropertyFormValues(propertyValuesMap, definitions),
    [definitions],
  )

  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const editableDefinitionsSet = useMemo(
    () => new Set((editableDefinitions || []).map((definition: PropertyDefinition) => definition.propertyName)),
    [editableDefinitions],
  )

  const {
    propertyValuesMap,
    setPropertyValue,
    revertPropertyValue,
    discardChanges,
    commitChanges,
    propertyValuesErrors,
  } = useEditCustomProperties({
    propertyValues: editingTargets.map(t => t.properties || {}),
    editableDefinitions,
    propertyValueValidator,
  })

  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const hasUnsavedChanges = useMemo(() => Object.values(propertyValuesMap).some(p => p.changed), [propertyValuesMap])
  const {canClose, discardWithConfirmation} = useDiscardConfirmation({
    hasUnsavedChanges,
    onConfirm: () => {
      discardChanges()
      onClose?.()
    },
  })

  const saveMutation = useSetPropertiesValuesMutation({
    setValuesPath,
    onSuccess: () => {
      commitChanges()
      onSuccess()
    },
    onError: setErrorMessage,
  })

  const validateForm = () => {
    const errorResults = formValidator?.(propertyValuesMap) || {}
    const isValid = Object.keys(errorResults).length === 0

    if (!isValid) {
      propertyValuesErrors(errorResults)
    }

    return isValid
  }

  const onSubmit = async () => {
    // Clear any previous error here (instead of in onMutate) so we also handle
    // the case where the mutation is never called (e.g. no properties changed).
    // Doing this inside the mutation would treat a no-op as success and trigger
    // onSuccess, which is not desired.
    setErrorMessage('')
    if (saveMutation.isPending) {
      return
    }

    const formIsValid = validateForm()
    if (!formIsValid || Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      const targetIds = editingTargets.map(target => target.id)
      saveMutation.mutate({targetIds, changedProperties})
    } else {
      onClose?.()
    }
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  if (!editingTargets.length) {
    return null
  }

  return (
    <>
      {errorMessage && (
        <div className="mb-3">
          <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
        </div>
      )}
      {renderPageHeader({canClose})}
      <PropertiesValuesForm>
        <PropertiesValuesForm.Filter
          value={filterText}
          onChange={setFilterText}
          definitionsCount={filteredDefinitions.length}
        />

        {filteredDefinitions.length === 0 ? (
          <PropertiesValuesForm.EmptyState />
        ) : (
          <PropertiesValuesTable
            definitions={filteredDefinitions}
            propertyValuesMap={propertyValuesMap}
            rowRenderer={(rowProps, setRef) => {
              const {definition} = rowProps
              const isEditable = editableDefinitionsSet.has(definition.propertyName)

              if (isEditable) {
                return (
                  <PropertiesValuesTable.EditValueRow
                    setRef={setRef}
                    {...rowProps}
                    required={definition.requireExplicitValues}
                    onChange={setPropertyValue}
                    onReset={revertPropertyValue}
                    showUndo
                  />
                )
              }
              return (
                <PropertiesValuesTable.ReadValueRow
                  {...rowProps}
                  required={definition.requireExplicitValues}
                  managedBySource={managedBySource}
                />
              )
            }}
          />
        )}

        <PropertiesValuesForm.ActionsContainer>
          {actionLabels.save && (
            <Button type="button" onClick={onSubmit} variant="primary">
              {actionLabels.save}
            </Button>
          )}

          {actionLabels.cancel && (
            <Button type="button" onClick={discardWithConfirmation} variant="default">
              {actionLabels.cancel}
            </Button>
          )}
        </PropertiesValuesForm.ActionsContainer>
      </PropertiesValuesForm>
    </>
  )
}

try{ PropertiesEditingPage.displayName ||= 'PropertiesEditingPage' } catch {}